"use client"

import { useTranslation } from "react-i18next"
import { DateTime } from "luxon"
import { Mail, MessageCircle, Send, Smartphone, CloudOff, CornerDownLeft } from "lucide-react"
import { Badge } from "@/components/ui/badge"
import { useQuery } from "@tanstack/react-query"
import { useAppointmentModal } from "@/contexts/AppointmentModalContext"
import { fetchAppointmentMessages } from "@/lib/services/appointmentModalService"

// Communication trace for THIS appointment — record-scoped rows from the
// central c_communication ledger, delivered inside the appointment-details
// response (backend docs/codebase/messenger/README.md §8). The former
// v0-generated Call History tab is intentionally gone: no telephony data
// source exists, and we do not ship fake tabs.

function ChannelIcon({ channel }: { channel: string }) {
  const cls = "w-4 h-4"
  switch (channel) {
    case "telegram":
      return <Send className={`${cls} text-sky-500`} />
    case "whatsapp":
      return <MessageCircle className={`${cls} text-green-500`} />
    case "sms":
      return <MessageCircle className={`${cls} text-gray-500`} />
    case "push":
      return <Smartphone className={`${cls} text-gray-500`} />
    default:
      return <Mail className={`${cls} text-gray-500`} />
  }
}

function StatusBadge({ status, error }: { status: string; error: string | null }) {
  const { t } = useTranslation()
  if (status === "sent") {
    return (
      <Badge variant="outline" className="border-green-300 text-green-700 bg-green-50">
        {t("appointment.messages.status_sent", "Sent")}
      </Badge>
    )
  }
  if (status === "error") {
    return (
      <Badge
        variant="outline"
        className="border-red-300 text-red-700 bg-red-50"
        title={error ?? undefined}
      >
        {t("appointment.messages.status_error", "Failed")}
      </Badge>
    )
  }
  if (status === "received") {
    return (
      <Badge variant="outline" className="border-sky-300 text-sky-700 bg-sky-50">
        {t("appointment.messages.status_received", "Received")}
      </Badge>
    )
  }
  if (status === "expired") {
    return (
      <Badge variant="outline" className="text-gray-500">
        {t("appointment.messages.status_expired", "Expired")}
      </Badge>
    )
  }
  return (
    <Badge variant="outline" className="text-gray-500">
      {t("appointment.messages.status_queued", "Queued")}
    </Badge>
  )
}

export function MessagesSection() {
  const { t } = useTranslation()
  const { appointment, appointmentId } = useAppointmentModal()
  const recordId = appointmentId ?? appointment?.id ?? null
  // Fresh on every open of the tab (backend /record/{id}/messages, never cached).
  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["appointmentMessages", recordId],
    queryFn: () => fetchAppointmentMessages(Number(recordId)),
    enabled: recordId != null,
    staleTime: 0,
    retry: 1,
  })
  const trace = isError ? null : data

  const slugLabel = (slug: string) => {
    const fallback = slug ? slug.replaceAll("_", " ") : "message"
    const pretty = fallback.charAt(0).toUpperCase() + fallback.slice(1)
    return t(`appointment.messages.slug_${slug}`, pretty)
  }

  const formatWhen = (item: { sent_at: string | null; created_at: string | null }) => {
    const iso = item.sent_at ?? item.created_at
    if (!iso) return "—"
    const dt = DateTime.fromISO(iso)
    return dt.isValid ? dt.toFormat("dd.MM.yyyy HH:mm") : "—"
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <h3 className="text-sm font-medium text-gray-500 mb-4">
        {t("appointment.messages.sent_messages", "Sent Messages")}
      </h3>

      {isLoading ? (
        <div className="text-sm text-gray-500 py-8 text-center">{t("common.loading", "Loading...")}</div>
      ) : !trace || !trace.available ? (
        <div className="flex flex-col items-center gap-3 text-sm text-gray-500 py-8">
          <div className="flex items-center gap-2">
            <CloudOff className="w-4 h-4" />
            {t(
              "appointment.messages.unavailable",
              "Message history is temporarily unavailable — please try again in a minute.",
            )}
          </div>
          <button
            type="button"
            className="text-xs text-blue-600 hover:underline disabled:opacity-50"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {t("inbox.retry", "Retry")}
          </button>
        </div>
      ) : trace.items.length === 0 ? (
        <div className="text-sm text-gray-500 py-8 text-center">
          {t(
            "appointment.messages.empty",
            "Nothing has been sent about this appointment yet. Confirmations and reminders will appear here.",
          )}
        </div>
      ) : (
        <div className="border rounded-md divide-y">
          {trace.items.map((item, index) => (
            <div key={index} className="flex items-center gap-3 px-4 py-3">
              <ChannelIcon channel={item.channel} />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium flex items-center gap-1">
                  {item.direction === "in" && (
                    <CornerDownLeft className="w-3 h-3 text-sky-500" aria-label={t("appointment.messages.inbound", "from client")} />
                  )}
                  {slugLabel(item.slug)}
                </div>
                <div className="text-xs text-gray-500 truncate">{item.preview || item.to_ref}</div>
              </div>
              <div className="text-xs text-gray-500 whitespace-nowrap">{formatWhen(item)}</div>
              <StatusBadge status={item.status} error={item.error} />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
